import { EnvironmentConfig, Environment } from './types';
import { developmentConfig } from './development';
import { stagingConfig } from './staging';
import { productionConfig } from './production';
import { validateConfig, validateRequiredEnvVars } from './validation';

/**
 * Environment configuration loader and manager
 */
class ConfigManager {
  private static instance: ConfigManager;
  private config: EnvironmentConfig | null = null;
  private environment: Environment;

  private constructor() {
    this.environment = this.determineEnvironment();
  }

  public static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  /**
   * Gets the current environment configuration
   */
  public getConfig(): EnvironmentConfig {
    if (!this.config) {
      this.config = this.loadConfig();
      this.validateAndOverrideWithEnvVars();
    }
    return this.config;
  }

  /**
   * Gets the current environment
   */
  public getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Reloads the configuration (useful for testing)
   */
  public reloadConfig(): EnvironmentConfig {
    this.config = null;
    return this.getConfig();
  }

  /**
   * Determines the current environment from various sources
   */
  private determineEnvironment(): Environment {
    // Priority order:
    // 1. VITE_ENVIRONMENT environment variable
    // 2. NODE_ENV environment variable
    // 3. Default to development

    const viteEnv = import.meta.env.VITE_ENVIRONMENT as Environment;
    const nodeEnv = import.meta.env.NODE_ENV;

    if (viteEnv && this.isValidEnvironment(viteEnv)) {
      return viteEnv;
    }

    if (nodeEnv === 'production') {
      return 'production';
    }

    if (nodeEnv === 'test') {
      return 'development'; // Use development config for tests
    }

    return 'development';
  }

  /**
   * Loads the base configuration for the current environment
   */
  private loadConfig(): EnvironmentConfig {
    switch (this.environment) {
      case 'development':
        return { ...developmentConfig };
      case 'staging':
        return { ...stagingConfig };
      case 'production':
        return { ...productionConfig };
      default:
        console.warn(`Unknown environment: ${this.environment}, falling back to development`);
        return { ...developmentConfig };
    }
  }

  /**
   * Validates configuration and overrides with environment variables
   */
  private validateAndOverrideWithEnvVars(): void {
    if (!this.config) return;

    // Override with environment variables if provided
    this.overrideWithEnvVars();

    // Validate the final configuration
    validateConfig(this.config);

    // Log configuration in development
    if (this.config.enableDebugMode) {
      console.log('Loaded configuration:', {
        environment: this.config.environment,
        apiBaseUrl: this.config.apiBaseUrl,
        cognitoRegion: this.config.cognitoRegion,
        enableDebugMode: this.config.enableDebugMode,
      });
    }
  }

  /**
   * Overrides configuration with environment variables
   */
  private overrideWithEnvVars(): void {
    if (!this.config) return;

    const envOverrides: Partial<EnvironmentConfig> = {};

    // API Configuration
    if (import.meta.env.VITE_API_BASE_URL) {
      envOverrides.apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    }
    if (import.meta.env.VITE_API_TIMEOUT) {
      envOverrides.apiTimeout = parseInt(import.meta.env.VITE_API_TIMEOUT, 10);
    }

    // Cognito Configuration
    if (import.meta.env.VITE_COGNITO_USER_POOL_ID) {
      envOverrides.cognitoUserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
    }
    if (import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID) {
      envOverrides.cognitoUserPoolClientId = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID;
    }
    if (import.meta.env.VITE_COGNITO_HOSTED_UI_URL) {
      envOverrides.cognitoHostedUiUrl = import.meta.env.VITE_COGNITO_HOSTED_UI_URL;
    }
    if (import.meta.env.VITE_COGNITO_REGION) {
      envOverrides.cognitoRegion = import.meta.env.VITE_COGNITO_REGION;
    }

    // CDN Configuration
    if (import.meta.env.VITE_IMAGES_CDN_URL) {
      envOverrides.imagesCdnUrl = import.meta.env.VITE_IMAGES_CDN_URL;
    }
    if (import.meta.env.VITE_STATIC_ASSETS_CDN_URL) {
      envOverrides.staticAssetsCdnUrl = import.meta.env.VITE_STATIC_ASSETS_CDN_URL;
    }

    // Feature Flags
    if (import.meta.env.VITE_ENABLE_DEBUG_MODE !== undefined) {
      envOverrides.enableDebugMode = import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true';
    }
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== undefined) {
      envOverrides.enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
    }
    if (import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING !== undefined) {
      envOverrides.enablePerformanceMonitoring = import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true';
    }
    if (import.meta.env.VITE_ENABLE_ERROR_REPORTING !== undefined) {
      envOverrides.enableErrorReporting = import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true';
    }

    // Apply overrides
    Object.assign(this.config, envOverrides);
  }

  private isValidEnvironment(env: string): env is Environment {
    return ['development', 'staging', 'production'].includes(env);
  }
}

// Singleton instance
const configManager = ConfigManager.getInstance();

/**
 * Gets the current environment configuration
 */
export function getConfig(): EnvironmentConfig {
  return configManager.getConfig();
}

/**
 * Gets the current environment
 */
export function getEnvironment(): Environment {
  return configManager.getEnvironment();
}

/**
 * Checks if the current environment is development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Checks if the current environment is staging
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging';
}

/**
 * Checks if the current environment is production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Gets a specific configuration value
 */
export function getConfigValue<K extends keyof EnvironmentConfig>(key: K): EnvironmentConfig[K] {
  return getConfig()[key];
}

/**
 * Validates that all required environment variables are present
 */
export function ensureRequiredEnvVars(): void {
  const requiredVars = [
    'VITE_API_BASE_URL',
    'VITE_COGNITO_USER_POOL_ID',
    'VITE_COGNITO_USER_POOL_CLIENT_ID',
    'VITE_IMAGES_CDN_URL'
  ];

  // Only validate in production
  if (isProduction()) {
    validateRequiredEnvVars(requiredVars);
  }
}

// Export types and configurations for testing and advanced usage
export type { EnvironmentConfig, Environment, ConfigValidationResult } from './types';
export { developmentConfig } from './development';
export { stagingConfig } from './staging';
export { productionConfig } from './production';
export { validateConfig } from './validation';

// Initialize configuration on module load
try {
  ensureRequiredEnvVars();
  configManager.getConfig(); // This will load and validate the configuration
} catch (error) {
  console.error('Configuration initialization failed:', error);
  // In development, we can continue with default config
  if (!isDevelopment()) {
    throw error;
  }
}